import type { BigNumber } from "@yjsgames/bignumber";
import type { ResolvedReward } from "./RewardResolver.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";

/**
 * 보상 획득 팝업 그리드용 변환 (순수 — Phaser/i18n 비의존).
 *
 * `ResolvedReward`는 재화가 합산값이고 모듈은 `moduleDrops[]`((id, rarity))로 풀려 있다. 이를 그리드 1셀 = 1
 * `RewardLineItem`으로 변환한다 — 표시 문자열(t()/formatBig)·아이콘은 View(GameScene.toRewardCell) 책임이라
 * 여기서는 raw 식별자(kind/id/rarity/수량)만 담는다 (`RewardResolver`가 확립한 순수/표시 분리 컨벤션).
 * 같은 모듈이라도 등급 또는 실제 획득 조각(shapeId+footprintId)이 다르면 별도 셀로 묶는다.
 */
export type RewardLineKind = "gold" | "diamond" | "upgradeStone" | "rebirthPoint" | "module" | "artifact";

/** 모듈 조각의 연결 모양 — 획득 모달이 실제 인스턴스 파이프 모양을 그리는 데 쓴다(아이콘 대용). */
export interface RewardShape {
  readonly shapeId: string;
  readonly footprintId: string;
}

/** 보상 한 줄 — 표시용 raw 데이터. */
export interface RewardLineItem {
  readonly kind: RewardLineKind;
  /** module=definitionId, artifact=artifactId. 재화 종류는 미사용. */
  readonly id?: string;
  /** module일 때 rarity (아이콘 테두리색·상세 표시) — 추첨된 등급(drop.rarity). */
  readonly rarity?: ModuleRarity;
  /** gold/diamond 합산값(BigNumber). 정수 재화/묶음 수량은 `amount`를 쓴다. */
  readonly amountBig?: BigNumber;
  /** upgradeStone/rebirthPoint 합산값, module/artifact 묶음 count. gold/diamond는 0(amountBig 사용). */
  readonly amount: number;
  /**
   * module일 때 실제 획득 인스턴스의 조각 모양. 같은 모듈/등급이라도 shapeId+footprintId가 다르면 별도 셀로 나뉜다.
   * 없으면 모달이 기존 획일 아이콘으로 폴백.
   */
  readonly shape?: RewardShape;
}

/**
 * ResolvedReward → 그리드 라인 아이템 배열 (순수).
 * - 재화(gold/diamond/upgradeStone/rebirthPoint): 0 초과만 1줄.
 * - module: (definitionId, rarity, shapeId, footprintId)별 묶어 count 집계 (첫 등장 순서 보존, ×N).
 *   shapes가 없으면 기존처럼 (definitionId, rarity) 기준으로 묶는다.
 * - artifact: id별 amount 합산.
 * 표시 순서: gold → diamond → upgradeStone → rebirthPoint → module → artifact.
 */
export function toRewardLineItems(
  r: ResolvedReward,
  shapes: readonly RewardShape[] = [],
): RewardLineItem[] {
  const items: RewardLineItem[] = [];
  if (!r.gold.isZero()) items.push({ kind: "gold", amountBig: r.gold, amount: 0 });
  if (!r.diamond.isZero()) items.push({ kind: "diamond", amountBig: r.diamond, amount: 0 });
  if (r.upgradeStone > 0) items.push({ kind: "upgradeStone", amount: r.upgradeStone });
  if (r.rebirthPoint > 0) items.push({ kind: "rebirthPoint", amount: r.rebirthPoint });

  // 모듈: drop 순서와 지급 인스턴스 순서를 짝지어 실제 조각 단위까지 집계한다.
  const moduleCounts = new Map<
    string,
    { id: string; rarity: ModuleRarity; amount: number; shape?: RewardShape }
  >();
  let moduleIndex = 0;
  for (const d of r.moduleDrops) {
    const shape = shapes[moduleIndex++];
    const shapeKey = shape ? `${shape.shapeId}|${shape.footprintId}` : "";
    const key = `${d.id}|${d.rarity}|${shapeKey}`;
    const cur = moduleCounts.get(key);
    if (cur) cur.amount += 1;
    else {
      moduleCounts.set(key, {
        id: d.id,
        rarity: d.rarity,
        amount: 1,
        ...(shape ? { shape: { shapeId: shape.shapeId, footprintId: shape.footprintId } } : {}),
      });
    }
  }
  for (const { id, rarity, amount, shape } of moduleCounts.values()) {
    items.push({ kind: "module", id, rarity, amount, ...(shape ? { shape } : {}) });
  }

  // 유물: id별 amount 합산 (미구현 placeholder — 표시용).
  const artifactCounts = new Map<string, number>();
  for (const a of r.artifacts) artifactCounts.set(a.id, (artifactCounts.get(a.id) ?? 0) + a.amount);
  for (const [id, amount] of artifactCounts) {
    items.push({ kind: "artifact", id, amount });
  }

  return items;
}

/**
 * 모듈 라인 아이템에 실제 생성된 인스턴스의 조각 모양을 후주입 (순수).
 *
 * `toRewardLineItems`는 drop(모양 없음)에서 파생되므로 모양을 모른다. grant가 반환한 생성 인스턴스
 * 배열을 받아 module 라인 아이템에 **등장순으로 1:1 매칭**해 `shape`를 붙인다. ×N으로 묶인 셀은
 * 대표 1개(첫 매칭 인스턴스) 모양만 담는다(1차 단순화 — 셀 내부 멀티 모양은 범위 밖).
 * 비모듈 아이템·매칭할 인스턴스가 없는 module은 무변경(모달이 기존 아이콘으로 폴백).
 */
export function attachShapes(
  items: readonly RewardLineItem[],
  instances: readonly RewardShape[],
): RewardLineItem[] {
  let mi = 0;
  return items.map((it) => {
    if (it.kind !== "module" || mi >= instances.length) return it;
    const src = instances[mi++]!;
    return { ...it, shape: { shapeId: src.shapeId, footprintId: src.footprintId } };
  });
}
